import { IContentInfo } from '../../ts/models/IContentInfo';
import { IDataSourceDetailInfo, IDataSourceInfo } from '../../ts/models/IDataSourceInfo';
import { DataSources } from '../../ts/resources/Datasources';

export type CommonDatasourceInfo = Pick<IDataSourceInfo,
  'contentType' |
  'workbook' |
  'owner' |
  'hasExtracts' |
  'lastRefreshedAt' |
  'certificationDetails' |
  'extractEncryptionState' |
  'dataQualityIndicators'  |
  'extractCreationPending'
>;

export type AnyDatasourceType = IDataSourceInfo | IDataSourceDetailInfo | IContentInfo;

/**
 * A Unified Data Source can exist in VizPortal Client as one of a few different types, depending on which API
 * it was fetched from:
 *  - IDataSourceInfo (getDatasources)
 *  - IDataSourceDetailInfo (getDatasource)
 *  - IContentInfo (exploreContent)
 *
 *  NOTE: for "data connections" (IDataConnectionInfo), we use
 *
 * In the explorer and other places in the content browsing experience, we want to use a data source
 * object in a consistent manner regardless of its original type, so we map its fields to this common
 * interface when passing it down into the view components.
 */
export function getCommonDatasourceInfo(datasource: AnyDatasourceType) : CommonDatasourceInfo {

  // This also covers the case where it's already a CommonDatasourceInfo
  if (DataSources.isUnifiedDatasource(datasource)) {
    return {
      contentType:                'datasource',
      workbook:                   datasource.workbook,
      hasExtracts:                datasource.hasExtracts,
      lastRefreshedAt:            datasource.lastRefreshedAt,
      owner:                      datasource.owner,
      certificationDetails:       datasource.certificationDetails,
      extractEncryptionState:     datasource.extractEncryptionState,
      dataQualityIndicators:      datasource.dataQualityIndicators,
      extractCreationPending:     datasource.extractCreationPending
    };
  }

  // IContentInfo has the information inside the `contentDetail` property.
  else if (isContentInfo(datasource)) {
    return {
      contentType:                'datasource',
      workbook:                   datasource.contentDetail.workbook || null,
      hasExtracts:                datasource.contentDetail.hasExtracts || null,
      lastRefreshedAt:            datasource.contentDetail.lastRefreshedAt || null,
      owner:                      datasource.owner,
      certificationDetails:       datasource.contentDetail.certificationDetails || null,
      extractEncryptionState:     datasource.contentDetail.extractEncryptionState || null,
      dataQualityIndicators:      datasource.contentDetail.dataQualityIndicators || null,
      extractCreationPending:     datasource.contentDetail.extractCreationPending || null
    };
  }

  // we somehow got bad data, return an empty details object
  return {
    contentType: 'datasource',
    workbook: null,
    hasExtracts: false,
    lastRefreshedAt: null,
    owner: null,
    certificationDetails: null,
    extractEncryptionState: null,
    dataQualityIndicators: null,
    extractCreationPending: null
  };
}

function isContentInfo(datasource: any): datasource is IContentInfo {
  return !!(datasource.contentDetail);
}
